// Cliente WS de subtítulos: suscripción, reconexión con backoff y modelo de texto
// acumulado POR IDIOMA (réplica del player local del master: un solo bloque + auto-scroll
// que muestra las últimas N líneas). Convierte el contrato interim/final del back a un
// transcript acumulado por cada idioma de `translations`, para que el espectador pueda
// cambiar de idioma y ver todo el histórico. Agnóstico al framework — lo usa Stream.vue.
export default class SubtitleSocket {
    constructor({ wsBase, room, token, onUpdate, maxChars = 2000 }) {
        this.wsBase = wsBase;
        this.room = room;
        this.token = token;
        this.onUpdate = onUpdate; // (state) => void con { byLang, langs, sourceLang }
        this.maxChars = maxChars; // tope del buffer acumulado por idioma (evita crecer sin fin)
        this.ws = null;
        this.closed = false;
        this.retry = 0;
        this.pingTimer = null;
        this.committed = {}; // { idioma: frases cerradas concatenadas }
        this.current = {}; // { idioma: interim en curso }
    }

    connect() {
        if (!this.token) return; // sin sesión no conectamos (evita bucle de reconexión)
        const url = `${this.wsBase}/${this.room}?token=${encodeURIComponent(this.token)}`;
        this.ws = new WebSocket(url);
        this.ws.onopen = () => {
            this.retry = 0;
            this._startKeepalive(); // ping periódico contra el idle-timeout de Traefik
        };
        this.ws.onmessage = (e) => this._onMessage(JSON.parse(e.data));
        this.ws.onclose = (e) => {
            this._stopKeepalive();
            if (e && e.code === 1008) return; // 1008 = auth rechazada: NO reintentar
            this._reconnect();
        };
        this.ws.onerror = () => this.ws && this.ws.close();
    }

    _onMessage(msg) {
        // Mapa idioma->texto del mensaje. Si no hay translations, una sola pista (idioma de origen).
        const map =
            msg.translations && Object.keys(msg.translations).length
                ? msg.translations
                : { [msg.lang || "es"]: msg.text || "" };

        if (msg.final) {
            for (const [lang, text] of Object.entries(map)) {
                if (!text) continue;
                this.committed[lang] = (this.committed[lang] ? this.committed[lang] + " " : "") + text;
                if (this.committed[lang].length > this.maxChars) {
                    this.committed[lang] = this.committed[lang].slice(-this.maxChars);
                }
                this.current[lang] = "";
            }
        } else {
            for (const [lang, text] of Object.entries(map)) this.current[lang] = text || "";
        }

        // Construye el transcript completo por idioma (committed + interim en curso).
        const langs = Array.from(new Set([...Object.keys(this.committed), ...Object.keys(this.current)]));
        const byLang = {};
        for (const lang of langs) {
            const c = this.committed[lang] || "";
            const cur = this.current[lang] || "";
            byLang[lang] = c + (cur ? (c ? " " : "") + cur : "");
        }
        this.onUpdate({ byLang, langs, sourceLang: msg.lang || null });
    }

    _startKeepalive() {
        this._stopKeepalive();
        this.pingTimer = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send("ping");
        }, 25000); // el back consume estos frames en su receive_text() de keepalive
    }

    _stopKeepalive() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    _reconnect() {
        if (this.closed) return;
        const delay = Math.min(1000 * 2 ** this.retry++, 15000);
        setTimeout(() => !this.closed && this.connect(), delay);
    }

    close() {
        this.closed = true;
        this._stopKeepalive();
        if (this.ws) this.ws.close();
    }
}
